import { AnimatePresence } from "framer-motion";
import { useMemo, useCallback, useState, useEffect, useRef } from "react";
import { ToastContainer } from "react-toastify";

import { useCurrentMedia, useMediaStore, useCurrentModelChange } from "@rapid/store";
import FilePreview from "@rapid/components/file/Preview";
import { FileCarousel } from "@rapid/components/file/Carousel/FileCarousel";
import { useMedia } from "@rapid/contexts/MediaContext";
import { useCoreApp } from "@rapid/contexts/CoreAppContext";
import { BoundingBoxStrategyImpl } from "@rapid/utils/annotationStrategies/BoundingBox";
import { FilePreviewProvider } from "@rapid/contexts/FilePreviewProvider";
import { useTestWithMoreFiles } from "@rapid/useTestWithMoreFiles";
import { VideoTrimmer } from "@rapid/components/file/VideoTrimmer";
import { OutOfCreditsModal } from "@rapid/components/flow/shared/OutOfCreditsModal";
import { MediaItemStatus } from "@rapid/types/media";
import { getExtractedFrameIdsForMediaItem } from "@rapid/hooks/useMediaUtils";
import { useRandomSourceLoader } from "@rapid/hooks/useRandomSourceLoader";
import { NeedsEditingButton } from "./NeedsEditingButton";
import { ModelArchitecture } from "@rapid/components/flow/deploy/Steps/Model/types";
import { BatchProcessingModal } from "@rapid/components/flow/shared/Deploy/BatchProcessingModal";
import { useToast } from "@rapid/hooks/useToast";
import { useAnalytics } from "@rapid/contexts/AnalyticsContext";
import { AnalyticsFlowEvent } from "@rapid/analytics/types";

const BULK_UPLOAD_THRESHOLD = 10;
const BULK_UPLOAD_TOAST_ID = "bulk-upload-banner";
const BULK_UPLOAD_CONTAINER_ID = "bulk-upload-container";

const TestFilesVisualAreaContent = () => {
    const [isHovered, setIsHovered] = useState(false);
    const { isLoadingRandomSource } = useRandomSourceLoader();
    const { showLabels } = useCoreApp();
    const {
        testWithMoreFiles,
        uploadLimitError,
        clearUploadLimitError,
        isOutOfCredits,
        clearOutOfCreditsError
    } = useTestWithMoreFiles();
    const { mediaItemToTrim, pendingMediaItems } = useMedia();
    const currentMedia = useCurrentMedia();
    const media = useMediaStore();
    const currentModelChange = useCurrentModelChange();
    const { track } = useAnalytics();
    const toast = useToast();
    const isModelGated =
        !currentModelChange || currentModelChange.to.architecture === ModelArchitecture.SAM3;

    const [pendingBulkFiles, setPendingBulkFiles] = useState<File[]>([]);
    const pendingBulkFilesRef = useRef(pendingBulkFiles);
    pendingBulkFilesRef.current = pendingBulkFiles;

    const [batchModalFiles, setBatchModalFiles] = useState<File[]>([]);
    const [isBatchModalOpen, setIsBatchModalOpen] = useState(false);
    const batchJobType = batchModalFiles.some((f) => f.type.startsWith("video/"))
        ? "videos"
        : "images";

    const handleBulkUploadCancel = useCallback(() => {
        track({
            event: AnalyticsFlowEvent.DEPLOY_BULK_UPLOAD_CANCELLED,
            data: { fileCount: pendingBulkFilesRef.current.length }
        });
        setPendingBulkFiles([]);
    }, [track]);

    const handleBulkUploadContinue = useCallback(() => {
        const files = pendingBulkFilesRef.current;
        track({
            event: AnalyticsFlowEvent.DEPLOY_BULK_UPLOAD_CONTINUED,
            data: { fileCount: files.length }
        });
        setBatchModalFiles(files);
        setIsBatchModalOpen(true);
        setPendingBulkFiles([]);
    }, [track]);

    useEffect(() => {
        if (pendingBulkFiles.length > 0) {
            toast.custom({
                level: "purbo",
                toastId: BULK_UPLOAD_TOAST_ID,
                containerId: BULK_UPLOAD_CONTAINER_ID,
                autoClose: false,
                children: (
                    <>
                        <p className="font-semibold">
                            This batch is too large to process here ({pendingBulkFiles.length}{" "}
                            files)
                        </p>
                        <p>
                            Continue and we'll process the files in the background. We'll notify you
                            when they're ready to download.
                        </p>
                    </>
                ),
                actions: [
                    { label: "Cancel", onClick: handleBulkUploadCancel },
                    { label: "Continue", onClick: handleBulkUploadContinue }
                ]
            });
        } else {
            toast.dismiss(BULK_UPLOAD_TOAST_ID);
        }
    }, [pendingBulkFiles.length, toast, handleBulkUploadCancel, handleBulkUploadContinue]);

    const onAddMediaItems = useCallback(
        async (files: File[], onFinally?: () => void) => {
            const hasVideo = files.some((f) => f.type.startsWith("video/"));
            const isBulkUpload =
                files.length >= BULK_UPLOAD_THRESHOLD || (hasVideo && files.length >= 2);
            if (!isModelGated && isBulkUpload) {
                track({
                    event: AnalyticsFlowEvent.DEPLOY_BULK_UPLOAD_BANNER_SHOWN,
                    data: { fileCount: files.length, hasVideo }
                });
                setPendingBulkFiles(Array.from(files));
                onFinally?.();
                return;
            }

            await testWithMoreFiles(files);

            onFinally?.();
        },
        [isModelGated, testWithMoreFiles, track]
    );

    // Check if current file is still uploading/processing
    const isCurrentFileProcessing = useMemo(() => {
        if (!currentMedia?.file) return false;

        const currentFile = currentMedia.file;

        // Check if it's in pending items
        if (pendingMediaItems.some((item) => item.id === currentFile.id)) {
            return true;
        }

        // Check if it's uploading or processing
        if (
            currentFile.status === MediaItemStatus.UPLOADING ||
            currentFile.status === MediaItemStatus.PROCESSING
        ) {
            return true;
        }

        // For videos, check if extracted frames are still uploading
        if (currentFile.isVideo) {
            const extractedFrameIds = getExtractedFrameIdsForMediaItem(currentFile);
            const uploadingFrames = extractedFrameIds.some(
                (frameId) => media[frameId]?.status === MediaItemStatus.UPLOADING
            );
            if (uploadingFrames) {
                return true;
            }
        }

        return false;
    }, [currentMedia?.file, pendingMediaItems, media]);

    return (
        <AnimatePresence mode="wait">
            <div
                className="visualArea relative"
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
            >
                <ToastContainer
                    containerId={BULK_UPLOAD_CONTAINER_ID}
                    position="top-center"
                    className="!absolute !left-1/2 !top-4 z-[100] w-[85%] !-translate-x-1/2"
                    closeButton={false}
                    newestOnTop
                />
                <FilePreviewProvider>
                    <FileCarousel
                        className="h-full w-full"
                        onAddMediaItems={onAddMediaItems}
                        uploadLimitError={uploadLimitError}
                        clearUploadLimitError={clearUploadLimitError}
                        isLoadingRandomSource={isLoadingRandomSource}
                        isHovered={isHovered}
                    >
                        <FilePreview
                            key={`file-${currentMedia?.file.id}`}
                            forceHideLabels={!showLabels}
                            strategy={BoundingBoxStrategyImpl}
                            showProcessingOverlay={isCurrentFileProcessing}
                            processingOverlayText="Processing file..."
                        />
                        {/* Needs Editing button - floating over the canvas area */}
                        {!isCurrentFileProcessing && <NeedsEditingButton />}
                    </FileCarousel>
                </FilePreviewProvider>

                {/* Video Trimmer Modal */}
                {mediaItemToTrim?.validation?.needsTrimming === true && <VideoTrimmer />}

                {/* Out of Credits Modal */}
                <OutOfCreditsModal isOpen={isOutOfCredits} onClose={clearOutOfCreditsError} />

                {/* Batch Processing Modal — opened when user confirms bulk upload */}
                <BatchProcessingModal
                    isOpen={isBatchModalOpen}
                    onClose={() => setIsBatchModalOpen(false)}
                    jobType={batchJobType}
                    initialFiles={batchModalFiles}
                />
            </div>
        </AnimatePresence>
    );
};

export function DeployVisualAreaContent() {
    return <TestFilesVisualAreaContent />;
}
